import { Injectable } from '@nestjs/common';
import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { InjectRepository } from '@nestjs/typeorm';
import { Entity, Repository } from 'typeorm';
import { AllExpensesResponse, ExpensesResponse } from '@gtpl/shared-models/hrms';
import { ExpensesRequest } from './dto/expenses-request';
import { ExpensesDTO } from './dto/expenses-dto';
import { ExpensesAdapter } from './dto/expenses-adapter';
import { Expenses } from './entity/expenses-entity'

@Injectable()
export class ExpensesService {

    constructor(
        @InjectRepository(Expenses)
        private repository: Repository<Expenses>,
        private adapter: ExpensesAdapter,
    ){}

    async getExpenses(expensesId: number): Promise<Expenses>{
        const response = await this.repository.findOne({
            where: {expensesId: expensesId},
        });
        if(response){
            return response;
        }else{
            return null;
        }
    }

    async createExpenses(data: ExpensesDTO, isUpdate: boolean): Promise<ExpensesResponse>{
        try{
            if(!isUpdate){
                const entity = await this.getExpenses(data.expensesId);
                if(entity){
                    throw new ErrorResponse(11104, 'Expenses already exits');
                }
            }else{
                const expensesEntity = await this.getExpenses(data.expensesId);
                if(expensesEntity){
                    if(expensesEntity.expensesId != data.expensesId){
                        throw new ErrorResponse(11104, 'Expenses already exits');
                    }
                }
            }
            const convertedDto: Expenses = await this.adapter.convertDtoToEntity(data);
            const savedExpenses: Expenses = await this.repository.save(convertedDto);
            const convertedExpenses: ExpensesDTO = await this.adapter.convertEntityToDto(savedExpenses);

            if(convertedExpenses){
                const response = new ExpensesResponse(true, isUpdate? 11100: 11101, isUpdate? 'Expenses updated successfully': 'Expenses created successfully', convertedExpenses);
                return response;
            }else{
                throw new ErrorResponse(11106, 'Expenses is saved but error while converting it into DTO');
            }
        }catch(error){
            throw error;
        }
    }

    async getAllExpensess(): Promise<AllExpensesResponse>{
        try{
            const expensess: ExpensesDTO[] = [];
            const entities : Expenses[] = await this.repository.find({
                order: {expensesId: 'ASC'},
            });
            if(entities.length>0){
                entities.forEach(Entity => {
                    const expenses: ExpensesDTO = this.adapter.convertEntityToDto(Entity);
                    expensess.push(expenses); 
                });
                const response = new AllExpensesResponse(true, 11208, 'Expensess retrieved successfully', expensess);
                return response;
            }else{
                throw new ErrorResponse(99998, 'No Records Found');
            }
        }catch(error){
            throw error;
        }
    }

    async getExpensesById(id:number): Promise<Expenses>{
        const response = await this.repository.findOne({
            where: {expensesId: id},
        });
        if(response){
            return response;
        }else{
            return null;
        }
    }

    async activateOrDeactivateExpenses(req: ExpensesRequest): Promise<ExpensesResponse>{
        try{
            const expensesExists = await this.getExpensesById(req.expensesId);
            if(expensesExists){
                if(req.versionFlag != expensesExists.versionFlag){
                    throw new ErrorResponse(10113, 'Someone updatedt the current Expenses information. Refresh and try again');
                }else{
                    const expensesStatus = await this.repository.update(
                        {expensesId: req.expensesId},
                        {isActive: req.isActive, updatedUser: req.updatedUser},
                    );
                    if(expensesExists.isActive){
                        if(expensesStatus.affected){
                            const response: ExpensesResponse= new ExpensesResponse(true, 10115, 'Expenses is de-activated successfully');
                            return response;
                        }else{
                            throw new ErrorResponse(10111, 'Expenses is already de-activated');
                        }
                    }else{
                        if(expensesStatus.affected){
                            const response: ExpensesResponse = new ExpensesResponse(true, 10114, 'Expenses is activated successfully');
                            return response;
                        }else{
                            throw new ErrorResponse(10112, 'Expenses is already activated');
                        }
                    }
                }
            }else{
                throw new ErrorResponse(99998, 'No Records Found');
            }
        }catch(error){
            throw error;
        }
    }

    async getAllActiveExpensess(): Promise<AllExpensesResponse>{
        try{
            const expensess: ExpensesDTO[] = [];
            const entities: Expenses[] = await this.repository.find({
                order: {expensesId: 'ASC'}, where: {isActive: true},
            });
            if(entities.length>0){
                entities.forEach(Entity => {
                    const Expenses: ExpensesDTO = this.adapter.convertEntityToDto(Entity);
                    expensess.push(Expenses); 
                });
                const response = new AllExpensesResponse(true, 11208, 'Expensess retrieved successfully', expensess);
                return response;
            }else{
                throw new ErrorResponse(99998, 'No Records Found');
            }
        }catch(error){
            throw error;
        }
    }

}
